import { ActionTypes } from './constants'
import { addPages, applyPageMetadata } from './pageListUpdates'
import type { ProjectActions, ProjectsState } from './types'

const init: ProjectsState = {
  projectsList: [],
  archivedProjects: [],
  loading: true,
  error: null,
  successMessage: null,
  folders: [],
  drawingSets: [],
  proposals: [],
  pageDetails: null,
  subscription: null,
  subscriptionId: null,
  proFeatureError: null,
}

const projects = (state = init, action: ProjectActions): ProjectsState => {
  switch (action.type) {
    case ActionTypes.GET_SUBSCRIPTION_SUCCESS: {
      return {
        ...state,
        subscription: action.payload.subscription,
      }
    }
    case ActionTypes.SET_ERROR_MESSAGE: {
      return {
        ...state,
        error: action.payload.message,
      }
    }

    case ActionTypes.SET_PAGE_DETAILS: {
      return {
        ...state,
        pageDetails: action.payload.data,
      }
    }

    case ActionTypes.SET_PROPOSALS: {
      return {
        ...state,
        proposals: action.payload.proposals,
      }
    }

    case ActionTypes.SET_SUCCESS_MESSAGE: {
      return {
        ...state,
        successMessage: action.payload.message,
      }
    }

    case ActionTypes.CLOSE_SUCCESS_MESSAGE: {
      return {
        ...state,
        successMessage: null,
      }
    }
    case ActionTypes.DELETE_ARCHIVED_PROJECT_SUCCESS: {
      return {
        ...state,
        loading: false,
        archivedProjects: state.archivedProjects.filter((project) => project.id !== action.payload.id),
      }
    }

    case ActionTypes.RESTORE_ARCHIVED_PROJECT_SUCCESS: {
      return {
        ...state,
        loading: false,
        archivedProjects: state.archivedProjects.filter((project) => project.id !== action.payload.id),
      }
    }

    case ActionTypes.CLOSE_ERROR_MESSAGE: {
      return {
        ...state,
        error: null,
      }
    }

    case ActionTypes.GET_FOLDER_SUCCESS: {
      return {
        ...state,
        //loading: true,
        folders: action.payload.data,
      }
    }

    case ActionTypes.UPDATE_FOLDER_SUCCESS: {
      return {
        ...state,
        loading: false,
        folders: state.folders.map((folder) => (folder.id === action.payload.data.id ? action.payload.data : folder)),
      }
    }

    case ActionTypes.DELETE_FOLDER_ERROR: {
      return {
        ...state,
        loading: false,
        error: action.payload.message,
      }
    }

    case ActionTypes.DELETE_FOLDER_SUCCESS: {
      return {
        ...state,
        loading: false,
        folders: state.folders.filter((folder) => folder.id !== action.payload?.folderId!),
      }
    }

    case ActionTypes.ADD_FOLDER_SUCCESS: {
      return {
        ...state,
        loading: false,
        folders: [...state.folders.filter((folder) => folder.id !== action.payload.data.id), action.payload.data],
      }
    }

    case ActionTypes.GET_USER_PROJECTS_REQUEST: {
      return {
        ...state,
        loading: true,
      }
    }

    case ActionTypes.GET_USER_PROJECTS_SUCCESS: {
      // A project response fetched BEFORE a recent Set Active landed can
      // arrive after it — keep the locally-set active set over stale data.
      const recent = state.recentActiveSetChange
      const recentHolds = recent && Date.now() - recent.at < 15000
      return {
        ...state,
        projectsList: action.payload.projectsList.map((project) => {
          return {
            ...project,
            activeDrawingSetId:
              recentHolds && project.id === recent.projectId ? recent.drawingSetId : project.activeDrawingSetId,
            drawingPageList: project.drawingPageList
              ? project.drawingPageList.map((page) => {
                  return {
                    ...page,
                    takeoffShapes: page.takeoffShapes && page.takeoffShapes.length > 0 ? [...page.takeoffShapes] : [],
                  }
                })
              : [],
          }
        }),
        loading: false,
      }
    }

    case ActionTypes.GET_USER_PROJECTS_ERROR: {
      return {
        ...state,
        loading: false,
        projectsList: [],
        error: action.payload.message,
      }
    }

    case ActionTypes.GET_ARCHIVED_PROJECTS_REQUEST: {
      return {
        ...state,
        loading: true,
      }
    }

    case ActionTypes.GET_ARCHIVED_PROJECTS_SUCCESS: {
      return {
        ...state,
        archivedProjects: action.payload.archivedProjects,
        loading: false,
      }
    }

    case ActionTypes.GET_ARCHIVED_PROJECTS_ERROR: {
      return {
        ...state,
        loading: false,
        archivedProjects: [],
        error: action.payload.message,
      }
    }

    case ActionTypes.POST_PROJECT_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: [...state.projectsList, action.payload.data],
        error: '',
      }
    }

    case ActionTypes.POST_PROJECT_ERROR: {
      return {
        ...state,
        loading: false,
        projectsList: [...state.projectsList],
        error: action.payload.message,
      }
    }

    case ActionTypes.ADD_PAGE_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.data.projectId
            ? {
                ...item,
                drawingPageList: [
                  ...item.drawingPageList,
                  {
                    ...action.payload.data,
                  },
                ],
              }
            : item,
        ),
        error: '',
      }
    }

    case ActionTypes.DELETE_PAGE_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.projectId
            ? {
                ...item,
                drawingPageList: item.drawingPageList.filter((page) => page.id !== action.payload.id),
              }
            : item,
        ),
        error: '',
      }
    }

    case ActionTypes.EDIT_PAGE_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.data.projectId
            ? {
                ...item,
                drawingPageList: item.drawingPageList.map((page) =>
                  page.id === action.payload.data.id ? action.payload.data : page,
                ),
              }
            : item,
        ),
        error: '',
      }
    }

    case ActionTypes.EDIT_PROJECT_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: state.projectsList.map((item) => {
          if (item.id === action.payload.data.id) {
            return {
              ...item,
              name: action.payload.data.name,
            }
          }

          return item
        }),
        error: '',
      }
    }

    case ActionTypes.EDIT_PROJECT_ERROR: {
      return {
        ...state,
        loading: false,
        projectsList: [...state.projectsList],
        error: action.payload.message,
      }
    }

    case ActionTypes.DELETE_PROJECT_SUCCESS: {
      return {
        ...state,
        loading: false,
        projectsList: state.projectsList.filter((item) => item.id !== action.payload.id),
        error: '',
      }
    }

    case ActionTypes.DELETE_PROJECT_ERROR: {
      return {
        ...state,
        loading: false,
        projectsList: [...state.projectsList],
        error: action.payload.message,
      }
    }

    case ActionTypes.SET_SHOW_INFO: {
      return {
        ...state,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.page.id
            ? {
                ...item,
                drawingPageList: item.drawingPageList.map((page) =>
                  page.name === action.payload.page.name
                    ? {
                        ...page,
                        edit: {
                          ...page.edit,
                          showInfo: action.payload.showAgain,
                        },
                      }
                    : page,
                ),
              }
            : item,
        ),
      }
    }

    case ActionTypes.SET_EDIT: {
      return {
        ...state,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.page.id
            ? {
                ...item,
                drawingPageList: item.drawingPageList.map((page) =>
                  page.name === action.payload.page.name
                    ? {
                        ...page,
                        edit: action.payload.edit,
                      }
                    : page,
                ),
              }
            : item,
        ),
      }
    }

    case ActionTypes.DELETE_PROPOSAL_SUCCESS: {
      return {
        ...state,
        proposals: state.proposals.filter((proposal) => proposal.id !== action.payload.id),
      }
    }

    case ActionTypes.GET_DRAWING_SETS_SUCCESS: {
      return {
        ...state,
        drawingSets: action.payload.data,
      }
    }

    case ActionTypes.ADD_DRAWING_SET_SUCCESS: {
      return {
        ...state,
        drawingSets: [...state.drawingSets.filter((set) => set.id !== action.payload.data.id), action.payload.data],
      }
    }

    case ActionTypes.UPDATE_DRAWING_SET_SUCCESS: {
      return {
        ...state,
        drawingSets: state.drawingSets.map((set) => (set.id === action.payload.data.id ? action.payload.data : set)),
      }
    }

    case ActionTypes.DELETE_DRAWING_SET_SUCCESS: {
      return {
        ...state,
        drawingSets: state.drawingSets.filter((set) => set.id !== action.payload.setId),
      }
    }

    case ActionTypes.PAGE_METADATA_SUCCESS:
    case ActionTypes.PAGES_ADDED_SUCCESS: {
      const update = action.type === ActionTypes.PAGE_METADATA_SUCCESS ? applyPageMetadata : addPages
      return {
        ...state,
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.projectId
            ? { ...item, drawingPageList: update(item.drawingPageList ?? [], action.payload.pages) }
            : item,
        ),
      }
    }

    case ActionTypes.SET_ACTIVE_DRAWING_SET_SUCCESS: {
      return {
        ...state,
        // Remembered so an in-flight stale project fetch can't revert it.
        recentActiveSetChange: {
          projectId: action.payload.projectId,
          drawingSetId: action.payload.drawingSetId,
          at: Date.now(),
        },
        projectsList: state.projectsList.map((item) =>
          item.id === action.payload.projectId
            ? {
                ...item,
                activeDrawingSetId: action.payload.drawingSetId,
              }
            : item,
        ),
      }
    }

    case ActionTypes.SET_PRO_FEATURE_ERROR: {
      return {
        ...state,
        proFeatureError: action.payload.featureName,
      }
    }

    case ActionTypes.CLEAR_PRO_FEATURE_ERROR: {
      return {
        ...state,
        proFeatureError: null,
      }
    }

    default:
      return state
  }
}

export default projects
